import { useEffect, useRef } from 'react';

// Auto-grow the element's height in the editor when the user adds items,
// so pairs/options/etc. can be added without introducing an inner scrollbar.
//
// Only fires when `contentCount` INCREASES — resizing the element (user drag)
// or removing items must not trigger growth, otherwise it fights the user
// and traps the widget at a large size.
//
// The element only knows its current pct via props, so we scale by the
// scrollHeight/clientHeight ratio to derive the pct that would just fit.
const useEditorAutoGrow = ({
  enabled,
  scrollRef,
  currentHeightPct,
  contentCount,
  onAutoGrow,
  maxHeightPct = 95,
}) => {
  const prevCountRef = useRef(contentCount);
  // Stash the callback so we always call the latest without needing it in deps.
  const onAutoGrowRef = useRef(onAutoGrow);
  useEffect(() => { onAutoGrowRef.current = onAutoGrow; }, [onAutoGrow]);
  const currentHeightPctRef = useRef(currentHeightPct);
  useEffect(() => { currentHeightPctRef.current = currentHeightPct; }, [currentHeightPct]);

  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = contentCount;
    if (!enabled) return;
    if (!scrollRef?.current) return;
    if (typeof contentCount !== 'number') return;
    if (contentCount <= prev) return; // only grow on additions

    // Let the DOM commit the new item before measuring.
    const raf = requestAnimationFrame(() => {
      const fn = onAutoGrowRef.current;
      const node = scrollRef.current;
      const pct = currentHeightPctRef.current;
      if (typeof fn !== 'function' || !node || !pct || pct <= 0) return;
      const scrollH = node.scrollHeight;
      const clientH = node.clientHeight;
      if (clientH <= 0 || scrollH <= clientH + 1) return;
      const ratio = scrollH / clientH;
      const target = Math.min(maxHeightPct, pct * ratio);
      if (target > pct + 0.5) fn(target);
    });
    return () => cancelAnimationFrame(raf);
  }, [enabled, contentCount, maxHeightPct, scrollRef]);
};

export default useEditorAutoGrow;
